/**
 * Prediction Service - Handles all AI prediction-related operations in Firestore
 */

import { db } from '../firebase';
import { handleFirestoreError } from '../utils';
import {
	createCacheKey,
	fetchWithCache,
} from '../../../utils/firestoreCache';

const PREDICTION_CACHE_MS = 30_000;

// Helper function to extract timestamp from prediction entry
const getPredictionEntryTimestamp = (entry) => {
	if (!entry || typeof entry !== "object") {
		return undefined;
	}

	const candidateKeys = ["t", "ExactTime", "timestamp", "Timestamp", "time"];
	for (const key of candidateKeys) {
		const value = entry[key];
		if (typeof value === "number") {
			return value;
		}

		if (typeof value === "string") {
			const parsed = Number(value);
			if (!Number.isNaN(parsed)) {
				return parsed;
			}
		}
	}

	return undefined;
};

// Helper function to resolve prediction options
const resolvePredictionOptions = (input) => {
	if (typeof input === "boolean") {
		return { onlyCurrent: input, forceRefresh: false, cacheDurationMs: undefined };
	}

	if (input && typeof input === "object") {
		const { onlyCurrent = false, forceRefresh = false, cacheDurationMs } = input;
		return {
			onlyCurrent: Boolean(onlyCurrent),
			forceRefresh: Boolean(forceRefresh),
			cacheDurationMs:
				typeof cacheDurationMs === "number" && !Number.isNaN(cacheDurationMs)
					? cacheDurationMs
					: undefined,
		};
	}

	return { onlyCurrent: false, forceRefresh: false, cacheDurationMs: undefined };
};

/**
 * Gets the AI status for a user
 * @param {string} userID - The unique identifier for the user
 * @returns {Promise<object>} Object containing the AI status
 */
export async function getAIStatus(userID) {
  try {
    const doc = await db
      .collection("Users")
      .doc(userID)
      .collection("Settings")
      .doc("AIStatus")
      .get();
    
    if (doc.exists) {
      return doc.data();
    } else {
      // Return default AI status
      return {
        enabled: true,
        lastUpdated: new Date().getTime()
      };
    }
  } catch (error) {
    handleFirestoreError(error);
  }
}

/**
 * Gets all predictions for a user
 * @param {string} userId - The unique identifier for the user
 * @param {boolean|object} options - Options object or boolean for onlyCurrent
 * @param {boolean} options.onlyCurrent - Whether to fetch only current predictions
 * @param {boolean} options.forceRefresh - Whether to bypass cache
 * @param {number} options.cacheDurationMs - Custom cache duration
 * @returns {Promise<Array>} Array of prediction objects
 */
export async function getAllPredictions(userId, options = false) {
	const { onlyCurrent, forceRefresh, cacheDurationMs } = resolvePredictionOptions(options);

	const cacheKey = createCacheKey(
		"dbPredictions",
		userId,
		{ onlyCurrent }
	);

	return fetchWithCache(
		cacheKey,
		async () => {
			const currentMillis = Date.now();

			try {
				const snapshot = await db
					.collection("Users")
					.doc(userId)
					.collection("Predictions")
					.where("LastUpdated", ">", currentMillis - 86400000 * 2)
					.get();

				if (snapshot.empty) {
					return [];
				}

				const macs = snapshot
					.docs
					.map((doc) => doc.data()?.MAC)
					.filter((mac) => typeof mac === "string");

				const uniqueMacs = Array.from(new Set(macs));

				if (uniqueMacs.length === 0) {
					return [];
				}

				const animalSnapshots = await Promise.all(
					uniqueMacs.map(async (mac) => {
						const animalDoc = await db
							.collection("Users")
							.doc(userId)
							.collection("Animals")
							.doc(mac)
							.get();

						return { mac, animalDoc };
					})
				);

				const activeAnimals = new Map();
				for (const { mac, animalDoc } of animalSnapshots) {
					if (!animalDoc.exists) {
						continue;
					}

					const animalData = animalDoc.data();
					if (!animalData || animalData.Active === false) {
						continue;
					}

					activeAnimals.set(mac, animalData);
				}

				if (activeAnimals.size === 0) {
					return [];
				}

				const subCollections = ["Health", "Birth", "Heat"];

				const subCollectionPromises = Array.from(
					activeAnimals.entries()
				).map(async ([mac, animalData]) => {
					const baseRef = db
						.collection("Users")
						.doc(userId)
						.collection("Predictions")
						.doc(mac);

					const subSnapshots = await Promise.all(
						subCollections.map((subCollection) =>
							baseRef.collection(subCollection).get()
						)
					);

					const records = [];
					subSnapshots.forEach((subSnapshot) => {
						subSnapshot.forEach((doc) => {
							const data = doc.data();
							records.push({
								...data,
								ID: data.ID || animalData.ID,
								Group: data.Group || animalData.Group,
							});
						});
					});

					return records;
				});

				const allResults = await Promise.all(subCollectionPromises);
				const flattened = allResults.flat();

				if (onlyCurrent) {
					const threshold = Date.now() - 86400000;
					return flattened.filter((entry) => {
						const timestamp = getPredictionEntryTimestamp(entry);
						return timestamp === undefined || timestamp >= threshold;
					});
				}

				return flattened;
			} catch (error) {
				console.error("[predictionService][getAllPredictions] - Error getting predictions:", error);
				throw error;
			}
		},
		{
			ttlMs: cacheDurationMs ?? PREDICTION_CACHE_MS,
			forceRefresh,
		}
	);
}

/**
 * Gets current predictions for a user (last 24 hours)
 * @param {string} userId - The unique identifier for the user
 * @returns {Promise<Array>} Array of current prediction objects
 */
export async function getCurrentPredictions(userId) {
	return getAllPredictions(userId, { onlyCurrent: true });
}

/**
 * Gets all custom labels for a user
 * @param {string} userId - The unique identifier for the user
 * @returns {Promise<Array>} Array of custom label objects
 */
export async function getAllCustomLabels(userId) {
	try {
		const labels = [];

		await db
			.collection("Users")
			.doc(userId)
			.collection("Label")
			.get()
			.then((snapshot) => {
				snapshot.forEach((doc) => {
					labels.push(doc.data());
				});
			})
			.catch((e) => {
				throw e;
			});

		return labels;
	} catch (error) {
		handleFirestoreError(error);
		throw error;
	}
}

// Export with typo for backward compatibility
export const gettAllCustomLabels = getAllCustomLabels;

/**
 * Writes a new label for a prediction
 * @param {string} userId - The unique identifier for the user
 * @param {Object} prediction - The prediction data to save as a label
 * @param {string} prediction.ExactTime - Timestamp used in document ID
 * @param {string} prediction.MAC - MAC address used in document ID
 * @returns {Promise<void>}
 */
export async function writeNewLabel(userId, prediction) {
	try {
		await db
			.collection("Users")
			.doc(userId)
			.collection("Label")
			.doc(prediction.ExactTime + "_" + prediction.MAC)
			.set(prediction, { merge: true })
			.catch((e) => {
				throw e;
			});
	} catch (error) {
		handleFirestoreError(error);
		throw error;
	}
}

/**
 * Deletes a label
 * @param {string} userId - The unique identifier for the user
 * @param {Object} prediction - The prediction data to delete
 * @param {string} prediction.ExactTime - Timestamp used in document ID
 * @param {string} prediction.MAC - MAC address used in document ID
 * @returns {Promise<void>}
 */
export async function deleteLabel(userId, prediction) {
	try {
		await db
			.collection("Users")
			.doc(userId)
			.collection("Label")
			.doc(prediction.ExactTime + "_" + prediction.MAC)
			.delete()
			.catch((e) => {
				throw e;
			});
	} catch (error) {
		handleFirestoreError(error);
		throw error;
	}
}

/**
 * Marks a prediction as labeled (deletes it from Predictions subcollection)
 * @param {string} userId - The unique identifier for the user
 * @param {string} type - Type of prediction (Health, Birth, Heat)
 * @param {string|number} docId - Document ID of the prediction
 * @param {string} mac - MAC address of the animal's device
 * @returns {Promise<void>}
 */
export async function markAsLabeled(userId, type, docId, mac) {
	try {
		await db
			.collection("Users")
			.doc(userId)
			.collection("Predictions")
			.doc(mac)
			.collection(type)
			.doc(docId + "")
			.delete()
			.catch((e) => {
				throw e;
			});
	} catch (error) {
		handleFirestoreError(error);
		throw error;
	}
}

// Export with typo for backward compatibility
export const markAsLabled = markAsLabeled;

/**
 * Gets custom prediction thresholds for a user (Heat, Birth, Lameness)
 * @param {string} userID - The unique identifier for the user
 * @returns {Promise<{Heat: number, Birth: number, Lameness: number}>} Prediction threshold settings (values between 60-95)
 */
export async function getCustomPredictionThresholds(userID) {
	try {
		let thresholds = {
			Heat: 75, // Default 75%
			Birth: 75, // Default 75%
			Lameness: 70, // Default 70%
		};

		const doc = await db
			.collection("Users")
			.doc(userID)
			.collection("Account")
			.doc("PredictionThresholds")
			.get();

		if (doc.exists && doc.data()) {
			const data = doc.data();
			// Ensure values are within valid range (60-95)
			thresholds.Heat = Math.min(95, Math.max(60, data.Heat || 75));
			thresholds.Birth = Math.min(95, Math.max(60, data.Birth || 75));
			thresholds.Lameness = Math.min(95, Math.max(60, data.Lameness || 70));
		}

		return thresholds;
	} catch (e) {
		console.error("[predictionService][getCustomPredictionThresholds] - Error getting prediction thresholds:", e);
		// Return defaults on error
		return {
			Heat: 75,
			Birth: 75,
			Lameness: 70,
		};
	}
}

/**
 * Sets custom prediction thresholds for a user
 * @param {string} userID - The unique identifier for the user
 * @param {Object} thresholds - Prediction threshold settings to save
 * @param {number} thresholds.Heat - Heat threshold (60-95)
 * @param {number} thresholds.Birth - Birth threshold (60-95)
 * @param {number} thresholds.Lameness - Lameness threshold (60-95)
 * @returns {Promise<void>}
 */
export async function setCustomPredictionThresholds(userID, thresholds) {
	try {
		// Validate thresholds are within range
		const validatedThresholds = {
			Heat: Math.min(95, Math.max(60, thresholds.Heat || 75)),
			Birth: Math.min(95, Math.max(60, thresholds.Birth || 75)),
			Lameness: Math.min(95, Math.max(60, thresholds.Lameness || 70)),
		};

		await db
			.collection("Users")
			.doc(userID)
			.collection("Account")
			.doc("PredictionThresholds")
			.set(validatedThresholds, { merge: true })
			.catch((e) => {
				throw e;
			});
	} catch (error) {
		handleFirestoreError(error);
		throw error;
	}
}

/**
 * Writes a new prediction validation
 * @param {string} userId - The unique identifier for the user
 * @param {Object} validationData - The validation data to store
 * @returns {Promise<string>} The ID of the created validation document
 */
export async function writePredictionValidation(userId, validationData) {
	try {
		const validationId = `${validationData.animalMAC}_${validationData.predictionType}_${validationData.detectionTimestamp}`;
		
		// Helper function to recursively remove undefined values from an object
		const sanitizeObject = (obj) => {
			if (obj === null || obj === undefined) {
				return null;
			}
			if (Array.isArray(obj)) {
				return obj.map(sanitizeObject).filter(item => item !== undefined);
			}
			if (typeof obj === 'object') {
				const sanitized = {};
				for (const [key, value] of Object.entries(obj)) {
					if (value !== undefined) {
						sanitized[key] = sanitizeObject(value);
					}
				}
				return sanitized;
			}
			return obj;
		};

		// Sanitize the entire validation data to remove any undefined values
		const sanitizedValidationData = sanitizeObject(validationData);
		
		const validationDoc = {
			animalId: sanitizedValidationData.animalId,
			animalMAC: sanitizedValidationData.animalMAC,
			animalName: sanitizedValidationData.animalName,
			predictionType: sanitizedValidationData.predictionType,
			isCorrect: sanitizedValidationData.isCorrect,
			detectionTimestamp: sanitizedValidationData.detectionTimestamp,
			validatedAt: Date.now(),
			validatedBy: userId,
			notes: sanitizedValidationData.notes || "",
			detectionFields: sanitizedValidationData.detectionFields || {}
		};

		await db
			.collection("Users")
			.doc(userId)
			.collection("PredictionValidations")
			.doc(validationId)
			.set(validationDoc)
			.catch((e) => {
				throw e;
			});

		return validationId;
	} catch (error) {
		console.error("[predictionService][writePredictionValidation] - Error writing prediction validation:", error);
		handleFirestoreError(error);
		throw error;
	}
}

/**
 * Gets all prediction validations for a user
 * @param {string} userId - The unique identifier for the user
 * @returns {Promise<Array<Object>>} Array of validation objects
 */
export async function getPredictionValidations(userId) {
	try {
		const snapshot = await db
			.collection("Users")
			.doc(userId)
			.collection("PredictionValidations")
			.orderBy("validatedAt", "desc")
			.get()
			.catch((e) => {
				throw e;
			});

		const validations = [];
		snapshot.forEach((doc) => {
			validations.push({
				id: doc.id,
				...doc.data()
			});
		});

		return validations;
	} catch (error) {
		console.error("[predictionService][getPredictionValidations] - Error getting prediction validations:", error);
		handleFirestoreError(error);
		throw error;
	}
}

/**
 * Updates an existing prediction validation
 * @param {string} userId - The unique identifier for the user
 * @param {string} validationId - The ID of the validation to update
 * @param {Object} updates - The updates to apply
 * @returns {Promise<void>}
 */
export async function updatePredictionValidation(userId, validationId, updates) {
	try {
		await db
			.collection("Users")
			.doc(userId)
			.collection("PredictionValidations")
			.doc(validationId)
			.update({
				...updates,
				updatedAt: Date.now()
			})
			.catch((e) => {
				throw e;
			});
	} catch (error) {
		console.error("[predictionService][updatePredictionValidation] - Error updating prediction validation:", error);
		handleFirestoreError(error);
		throw error;
	}
}

/**
 * Deletes a prediction validation
 * @param {string} userId - The unique identifier for the user
 * @param {string} validationId - The ID of the validation document to delete
 * @returns {Promise<void>}
 */
export async function deletePredictionValidation(userId, validationId) {
	try {
		await db
			.collection("Users")
			.doc(userId)
			.collection("PredictionValidations")
			.doc(validationId)
			.delete()
			.catch((e) => {
				throw e;
			});
	} catch (error) {
		console.error("[predictionService][deletePredictionValidation] - Error deleting prediction validation:", error);
		handleFirestoreError(error);
		throw error;
	}
}

/**
 * Gets prediction validations for a specific animal and prediction type
 * @param {string} userId - The unique identifier for the user
 * @param {string} animalMAC - The animal's MAC address
 * @param {string} predictionType - The type of prediction
 * @returns {Promise<Array<Object>>} Array of validation objects
 */
export async function getPredictionValidationsForAnimal(userId, animalMAC, predictionType) {
	try {
		const snapshot = await db
			.collection("Users")
			.doc(userId)
			.collection("PredictionValidations")
			.where("animalMAC", "==", animalMAC)
			.where("predictionType", "==", predictionType)
			.orderBy("validatedAt", "desc")
			.get()
			.catch((e) => {
				throw e;
			});

		const validations = [];
		snapshot.forEach((doc) => {
			validations.push({
				id: doc.id,
				...doc.data()
			});
		});

		return validations;
	} catch (error) {
		console.error("[predictionService][getPredictionValidationsForAnimal] - Error getting prediction validations for animal:", error);
		handleFirestoreError(error);
		throw error;
	}
}

/**
 * Deletes all predictions and saves them to log
 * @param {string} userID - The unique identifier for the user
 * @returns {Promise<void>}
 */
export async function deleteAllPredictionsAndSaveToLog(userID) {
	try {
		const eventsSnapshot = await db
			.collection("Users")
			.doc(userID)
			.collection("Predictions")
			.get()
			.catch((e) => {
				throw e;
			});

		const events = eventsSnapshot.docs.map((doc) => doc.data());

		const deletePromises = events.map(async (event) => {
			const deletePromise = db
				.collection("Users")
				.doc(userID)
				.collection("Predictions")
				.doc(event.MAC)
				.delete()
				.catch((e) => {
					throw e;
				});

			event.Description = "inconspicuous";

			const logPromise = db
				.collection("Users")
				.doc(userID)
				.collection("Log")
				.add(event)
				.catch((e) => {
					throw e;
				});

			return Promise.all([deletePromise, logPromise]);
		});

		await Promise.all(deletePromises);
	} catch (error) {
		console.error("[predictionService][deleteAllPredictionsAndSaveToLog] - Error deleting predictions:", error);
		handleFirestoreError(error);
		throw error;
	}
} 